import styled from '@emotion/styled';
import { Global, css } from '@emotion/react';
import { createElement, FC } from 'react';
import { Button } from 'antd';
import { colorConstants } from 'config/colorConstants';

export const LENS_SETTINGS_DROPDOWN_CLASS = 'price-simulator-v3-lens-settings-dropdown';

const lensSettingsDropdownGlobalStyles = css`
    .${LENS_SETTINGS_DROPDOWN_CLASS} .ant-dropdown-menu {
        padding: 0;
        background: transparent;
        box-shadow: none;
        overflow: visible;
    }

    .${LENS_SETTINGS_DROPDOWN_CLASS}.ant-dropdown {
        overflow: visible;
    }

    .${LENS_SETTINGS_DROPDOWN_CLASS} .ant-dropdown-menu-root {
        overflow: visible;
    }
`;

export const StyledLensSettingsDropdownGlobalStyles: FC = () =>
    createElement(Global, { styles: lensSettingsDropdownGlobalStyles });

const lensTabInactiveText = colorConstants.axisLabelGrey;
const lensTabActiveBg = colorConstants.lensTabActiveBg;
const lensTabInactiveBg = colorConstants.lensTabInactiveBg;
const lensTabActiveBorder = colorConstants.violet;
const lensTabHeight = '32px';
const lensTabGap = '8px';
const lensTabMinWidth = '150px';

const lensActiveLightBg = '#F7F5FF';
const lensSelectedBorder = '#d4c9f7';
const lensDividerColor = 'rgba(0, 0, 0, 0.06)';

export const StyledLensSettingsSearch = styled.div`
    flex-shrink: 0;
    width: 100%;
`;

export const StyledLensTabsRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${lensTabGap};
    padding: 0px;
    min-width: 0;
`;

export const StyledLensTabsTrack = styled.div`
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
        height: 4px;
    }

    &::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background: rgba(0, 0, 0, 0.15);
    }
`;

export const StyledLensTabsInner = styled.div`
    display: flex;
    align-items: center;
    gap: ${lensTabGap};
    min-width: 100%;
`;

export const StyledSettingsButton = styled(Button)`
    &.ant-btn {
        flex-shrink: 0;
        width: ${lensTabHeight};
        height: ${lensTabHeight};
        min-width: ${lensTabHeight};
        padding: 0;
        border: 1px solid ${colorConstants.toolbarBorderGrey};
        border-radius: 6px;
        box-shadow: ${colorConstants.buttonDefaultShadow};
        color: ${colorConstants.blackTransparent88};
        background: ${colorConstants.white};
        display: inline-flex;
        align-items: center;
        justify-content: center;

        .anticon {
            font-size: 16px;
        }

        &:hover,
        &:focus,
        &:active {
            color: ${colorConstants.purpleFontPressed} !important;
            background: ${colorConstants.white} !important;
            border-color: ${colorConstants.purpleFontPressed} !important;
            box-shadow: ${colorConstants.buttonDefaultShadow};
        }

        &[aria-expanded='true'] {
            color: ${colorConstants.violet} !important;
            background: ${colorConstants.white} !important;
            border-color: ${colorConstants.violet} !important;
            box-shadow: ${colorConstants.buttonDefaultShadow};
        }
    }
`;

export const StyledLensTab = styled.button<{ isActive: boolean }>`
    position: relative;
    flex: 1 0 ${lensTabMinWidth};
    min-width: ${lensTabMinWidth};
    height: ${lensTabHeight};
    padding: 6px 12px;
    border: none;
    border-radius: ${({ isActive }) => (isActive ? '4px' : '6px')};
    background: ${({ isActive }) => (isActive ? lensTabActiveBg : lensTabInactiveBg)};
    color: ${({ isActive }) => (isActive ? lensTabActiveBorder : lensTabInactiveText)};
    font-size: 14px;
    line-height: 22px;
    font-weight: ${({ isActive }) => (isActive ? 600 : 400)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    cursor: pointer;
    transition:
        background-color 0.2s ease,
        color 0.2s ease;

    &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: ${({ isActive }) => (isActive ? '1px' : '0')};
        background: ${lensTabActiveBorder};
        border-radius: 0 0 3px 3px;
    }

    &:hover {
        background: ${({ isActive }) => (isActive ? lensTabActiveBg : lensTabInactiveBg)};
        color: ${({ isActive }) => (isActive ? lensTabActiveBorder : colorConstants.blackTitle)};
    }
`;

export const StyledLensSettingsPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 240px;
    flex-shrink: 0;
    height: 326px;
    padding: 12px 8px;
    border-radius: 12px;
    background: var(--Components-Dropdown-Global-colorBgElevated, ${colorConstants.white});
    box-shadow:
        0 6px 8px rgba(0, 0, 0, 0.08),
        0 3px 3px rgba(0, 0, 0, 0.12),
        0 9px 14px rgba(0, 0, 0, 0.05);
    overflow: hidden;
`;

export const LENS_CONFIGURATION_DROPDOWN_WIDTH = 560;
export const LENS_CONFIGURATION_DROPDOWN_HEIGHT = 412;
export const LENS_SETTINGS_PANEL_GAP = 3;

export const StyledLensSettingsDropdownWrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: flex-start;
    gap: ${LENS_SETTINGS_PANEL_GAP}px;
`;

export const StyledLensConfigurationDropdown = styled.div`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    box-sizing: border-box;
    width: ${LENS_CONFIGURATION_DROPDOWN_WIDTH}px;
    height: ${LENS_CONFIGURATION_DROPDOWN_HEIGHT}px;
    min-height: ${LENS_CONFIGURATION_DROPDOWN_HEIGHT}px;
    padding: 12px 8px;
    border-radius: 12px;
    background: var(--Components-Dropdown-Global-colorBgElevated, #fff);
    box-shadow:
        0 6px 16px 0 rgba(0, 0, 0, 0.08),
        0 3px 6px -4px rgba(0, 0, 0, 0.12),
        0 9px 28px 8px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    z-index: 2;
`;

export const StyledLensConfigurationLoading = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    width: 100%;
    min-height: 0;
`;

export const StyledNestDimensionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    padding-block: 4px;

    .nest-dimensions-label {
        font-size: 14px;
        line-height: 22px;
        font-weight: 400;
        color: rgba(0, 0, 0, 0.88);
        white-space: nowrap;
    }

    .ant-switch {
        min-width: 28px;
        height: 16px;
        line-height: 16px;
    }

    .ant-switch .ant-switch-handle {
        width: 12px;
        height: 12px;
    }

    .ant-switch.ant-switch-checked .ant-switch-handle {
        inset-inline-start: calc(100% - 14px);
    }
`;

export const StyledLensSettingsList = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const StyledLensSettingsItem = styled.div<{
    isDragging?: boolean;
    isDropTarget?: boolean;
    isExpanded?: boolean;
    isSelected?: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    height: 30px;
    padding: 4px 8px 4px 4px;
    border-radius: 4px;
    border: 1px solid ${({ isDropTarget }) => (isDropTarget ? colorConstants.grey : 'transparent')};
    background: ${({ isSelected }) => (isSelected ? lensActiveLightBg : 'transparent')};
    opacity: ${({ isDragging }) => (isDragging ? 0.7 : 1)};
    cursor: ${({ isDragging }) => (isDragging ? 'grabbing' : 'pointer')};

    .lens-item-main {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
        flex: 1;
    }

    .lens-item-label-group {
        display: flex;
        align-items: center;
        gap: 4px;
        min-width: 0;
        flex: 1;
    }

    .lens-item-drag-handle {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 12px;
        height: 12px;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.45);
        cursor: grab;

        &:active {
            cursor: grabbing;
        }
    }

    .custom-lens-user-icon {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        width: 12px;
        height: 12px;
        font-size: 12px;
    }

    .lens-label {
        min-width: 0;
        flex: 1;
        font-size: 14px;
        line-height: 22px;
        color: ${({ isSelected }) => (isSelected ? colorConstants.purpleFont : 'rgba(0, 0, 0, 0.88)')};
        font-weight: ${({ isSelected }) => (isSelected ? 500 : 400)};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

`;

export const StyledLensExpandButton = styled.button<{ isExpanded?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 12px;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
    flex-shrink: 0;

    svg {
        transform: rotate(${({ isExpanded }) => (isExpanded ? '90deg' : '0deg')});
        transition: transform 0.2s ease;
    }
`;

export const StyledLensSettingsDivider = styled.div`
    flex-shrink: 0;
    height: 8px;
    box-sizing: border-box;
    padding-block: 4px;

    &::after {
        content: '';
        display: block;
        height: 1px;
        background: ${lensDividerColor};
    }
`;

export const StyledCustomLensRow = styled.button<{ isDisabled?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 0 8px;
    border: 0;
    border-radius: 0;
    background: transparent;
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
    text-align: left;
    opacity: ${({ isDisabled }) => (isDisabled ? 0.45 : 1)};

    &:hover {
        background: ${({ isDisabled }) => (isDisabled ? 'transparent' : lensActiveLightBg)};
    }

    .plus-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 1px solid
            ${({ isDisabled }) => (isDisabled ? colorConstants.breadcrumbSlash : colorConstants.purpleFont)};
        color: ${({ isDisabled }) => (isDisabled ? colorConstants.breadcrumbSlash : colorConstants.purpleFont)};
        font-size: 10px;
        line-height: 1;
    }

    .custom-lens-label {
        font-size: 14px;
        line-height: 22px;
        color: ${({ isDisabled }) => (isDisabled ? colorConstants.breadcrumbSlash : colorConstants.purpleFont)};
        font-weight: 500;
    }
`;

export const StyledLensSettingsFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 4px;
    padding-top: 12px;
    border-top: 1px solid ${lensDividerColor};
`;

const lensFooterButtonStyles = `
    &.ant-btn {
        height: 32px;
        padding: 0 15px;
        border-radius: 8px;
        font-size: 14px;
        line-height: 22px;
        font-weight: 500;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
`;

export const StyledLensFooterResetButton = styled(Button)`
    ${lensFooterButtonStyles}

    &.ant-btn {
        width: 100px;
        min-width: 100px;
        box-shadow: 0 2px 0 rgba(0, 0, 0, 0.02);
    }

    &.ant-btn:not([disabled]) {
        color: var(--Colors-New-Neutral-Text-colorTextTertiary, #52525c);
        border: 1px solid var(--Components-Button-Component-defaultBorderColor, ${colorConstants.greyAlt});
        background: var(--Components-Button-Component-defaultBg, ${colorConstants.white});

        &:hover,
        &:focus {
            color: var(--Colors-New-Neutral-Text-colorTextTertiary, #52525c) !important;
            border-color: var(--Components-Button-Component-defaultBorderColor, ${colorConstants.greyAlt}) !important;
            background: ${lensActiveLightBg} !important;
        }
    }

    &.ant-btn[disabled],
    &.ant-btn[disabled]:hover,
    &.ant-btn[disabled]:focus,
    &.ant-btn[disabled]:active {
        color: rgba(0, 0, 0, 0.25) !important;
        border-color: #d9d9d9 !important;
        background: #f5f5f5 !important;
        opacity: 1;
        cursor: not-allowed;
    }
`;

export const StyledLensFooterApplyButton = styled(Button)`
    ${lensFooterButtonStyles}

    &.ant-btn {
        width: 108px;
        min-width: 108px;
        box-shadow: 0 2px 0 rgba(95, 73, 188, 0.1);
    }

    &.ant-btn.ant-btn-primary:not([disabled]) {
        color: ${colorConstants.white};
        border: 1px solid var(--Components-Button-Global-colorPrimary, ${colorConstants.purpleFont});
        background: var(--Components-Button-Global-colorPrimary, ${colorConstants.purpleFont});

        &:hover,
        &:focus {
            color: ${colorConstants.white} !important;
            border-color: ${colorConstants.purpleFont} !important;
            background: ${colorConstants.purpleFont} !important;
            opacity: 0.92;
        }
    }

    /* Match Ant Design primary disabled (same as Create Custom Lens save button). */
    &.ant-btn.ant-btn-primary[disabled],
    &.ant-btn.ant-btn-primary[disabled]:hover,
    &.ant-btn.ant-btn-primary[disabled]:focus,
    &.ant-btn.ant-btn-primary[disabled]:active {
        color: rgba(0, 0, 0, 0.25) !important;
        border-color: #d9d9d9 !important;
        background: #f5f5f5 !important;
        opacity: 1;
        cursor: not-allowed;
    }
`;

export const StyledLensContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 320px;
    margin-top: 16px;
    background: ${colorConstants.white};
    border-radius: 4px;
`;

export const StyledLensContextBannerFooter = styled.div`
    margin-top: auto;
`;

export const StyledLensConfigPanelBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-height: 0;
`;

export const StyledLensConfigPanelHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
`;

export const StyledLensConfigPanelDivider = styled.div`
    position: relative;
    flex-shrink: 0;
    height: 8px;
    box-sizing: border-box;

    &::after {
        content: '';
        position: absolute;
        top: 4px;
        left: 0;
        right: 0;
        height: 1px;
        background: ${lensDividerColor};
    }
`;

export const StyledLensConfigContentArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-height: 0;
`;

export const StyledLensConfigEditButton = styled.button`
    flex-shrink: 0;
    height: 32px;
    padding: 0 15px;
    border-radius: 8px;
    border: 1px solid var(--Components-Button-Component-defaultBorderColor, ${colorConstants.greyAlt});
    color: var(--Colors-New-Neutral-Text-colorTextTertiary, #52525c);
    background: var(--Components-Button-Component-defaultBg, ${colorConstants.white});
    font-size: 14px;
    line-height: 22px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 2px 0 rgba(0, 0, 0, 0.02);

    &:hover,
    &:focus {
        color: ${colorConstants.purpleFont};
        border-color: ${colorConstants.purpleFont};
        background: ${lensActiveLightBg};
        outline: none;
    }
`;

export const StyledLensConfigSystemRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    flex-shrink: 0;
`;

export const StyledLensConfigColumns = styled.div`
    display: flex;
    align-items: stretch;
    gap: 12px;
    flex: 1;
    min-height: 0;
`;

export const StyledLensConfigColumnDivider = styled.div`
    position: relative;
    flex-shrink: 0;
    width: 8px;
    align-self: stretch;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 4px;
        width: 1px;
        background: ${lensDividerColor};
    }
`;

export const StyledLensConfigFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    gap: 8px;
    padding: 0 4px;
    flex-shrink: 0;
    align-self: flex-end;
    width: 100%;
    box-sizing: border-box;
`;

export const StyledLensConfigColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
    min-height: 0;
`;

export const StyledLensConfigColumnHeader = styled.div<{ uiRole?: 'DIMENSION' | 'KPI' }>`
    flex-shrink: 0;
    min-height: 30px;
    padding: 4px 8px 4px 0;
    font-size: 14px;
    line-height: 22px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.88);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .column-count {
        font-weight: 700;
        color: ${colorConstants.toolbarLabelGrey};
    }
`;

export const StyledLensConfigColumnList = styled.div`
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-height: 0;

    .ant-checkbox-checked:not(.ant-checkbox-indeterminate) .ant-checkbox-inner {
        background-color: ${colorConstants.purpleFont} !important;
        border-color: ${colorConstants.purpleFont} !important;
    }

    /* Partial "(All)" selection: plain checkbox, no purple border/fill */
    .ant-checkbox-indeterminate .ant-checkbox-inner {
        background-color: ${colorConstants.white} !important;
        border-color: #d9d9d9 !important;
    }

    .ant-checkbox-indeterminate .ant-checkbox-inner::after {
        background-color:rgb(255, 255, 255) !important;
    }

    .ant-checkbox-wrapper:hover .ant-checkbox-checked:not(.ant-checkbox-indeterminate) .ant-checkbox-inner,
    .ant-checkbox-checked:not(.ant-checkbox-indeterminate):hover .ant-checkbox-inner,
    .ant-checkbox-checked:not(.ant-checkbox-indeterminate) .ant-checkbox-input:focus + .ant-checkbox-inner {
        border-color: ${colorConstants.purpleFont};
    }

    .ant-checkbox-indeterminate:hover .ant-checkbox-inner,
    .ant-checkbox-indeterminate .ant-checkbox-input:focus + .ant-checkbox-inner {
        border-color: #d9d9d9 !important;
    }
`;

export const StyledLensConfigItem = styled.div<{
    isDisabled?: boolean;
    isSelected?: boolean;
}>`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 30px;
    padding: 4px 8px 4px 4px;
    border-radius: 4px;
    border: 1px solid ${({ isSelected }) => (isSelected ? lensActiveLightBg : 'transparent')};
    background: ${({ isSelected }) => (isSelected ? lensActiveLightBg : 'transparent')};
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};

    ${({ isDisabled }) =>
        isDisabled &&
        `
        .lens-config-item-checkbox {
            pointer-events: none;
        }
    `}

    .lens-config-item-checkbox.ant-checkbox-wrapper {
        display: flex;
        width: 100%;
        margin-inline-start: 0;
        align-items: center;
        line-height: 22px;
    }

    .lens-config-item-checkbox .ant-checkbox {
        top: 0;
        align-self: center;
    }

    .lens-config-item-checkbox .ant-checkbox + span {
        flex: 1;
        min-width: 0;
        display: inline-flex;
        align-items: center;
        padding-inline-start: 8px;
        font-size: 14px;
        line-height: 22px;
        font-weight: 400;
        color: rgba(0, 0, 0, 0.88);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .lens-config-item-checkbox .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner {
        background: #EDEBF5 !important;
        border: var(--Components-Checkbox-Global-lineWidth, 1px) solid var(--Components-Checkbox-Global-colorBorder, #D9D9D9) !important;
    }

    .lens-config-item-checkbox .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner::after {
        border-color: rgba(0, 0, 0, 0.25) !important;
    }

    .item-label {
        flex: 1;
        font-size: 14px;
        line-height: 22px;
        font-weight: 400;
        color: rgba(0, 0, 0, 0.88);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .lens-config-item-checkbox .ant-checkbox-inner {
        width: 16px;
        height: 16px;
        border-radius: var(--Components-Checkbox-Global-borderRadiusSM, 4px);
    }

    .lens-config-item-checkbox:hover .ant-checkbox-inner,
    .lens-config-item-checkbox .ant-checkbox:hover .ant-checkbox-inner,
    .lens-config-item-checkbox .ant-checkbox-input:focus + .ant-checkbox-inner {
        border-color: #d9d9d9;
        box-shadow: none !important;
    }

    .lens-config-item-checkbox:hover .ant-checkbox-checked .ant-checkbox-inner,
    .lens-config-item-checkbox .ant-checkbox-checked:hover .ant-checkbox-inner,
    .lens-config-item-checkbox .ant-checkbox-checked .ant-checkbox-input:focus + .ant-checkbox-inner {
        background: var(--Components-Checkbox-Global-colorPrimary, #44338A) !important;
        border-color: var(--Components-Checkbox-Global-colorPrimary, #44338A) !important;
        box-shadow: none !important;
    }

    .lens-config-item-checkbox .ant-checkbox-wrapper:focus-within .ant-checkbox-inner,
    .lens-config-item-checkbox .ant-checkbox:after {
        box-shadow: none !important;
        border-color: transparent !important;
    }

    .lens-config-item-checkbox .ant-checkbox-checked .ant-checkbox-inner {
        background: var(--Components-Checkbox-Global-colorPrimary, #44338A);
        border-color: var(--Components-Checkbox-Global-colorPrimary, #44338A);
    }


    ${({ isSelected }) =>
        isSelected &&
        `
        .lens-config-item-checkbox > span:not(.ant-checkbox) {
            color: #44338A;
            font-weight: 500;
        }
    `}
`;

export const StyledLensConfigSystemItem = styled(StyledLensConfigItem)`
    width: fit-content;
    max-width: 100%;

    .lens-config-item-checkbox.ant-checkbox-wrapper {
        width: auto;
    }
`;
